import { API_BASE_URL } from "../config/apiConfig";
import { getAuthToken } from "../utils/apiUtils";

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Professional Profile API Service
const professionalProfileAPI = {
  // User Posts
  posts: {
    // Get user's posts - UPDATED ENDPOINT
    getUserPosts: async (userId, page = 1, limit = 5) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/posts/user/${userId}?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Get post engagement stats
    getPostStats: async (postId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/feed/posts/${postId}/stats`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : null;
      } catch (error) {
throw error;
      }
    },
  },

  // Research Rooms
  rooms: {
    // Get user's research rooms - FIXED ENDPOINT AND DATA PROCESSING
    getUserRooms: async (userId, status = "all") => {
      try {
        const params = new URLSearchParams({
          status,
        });

        // Use the correct endpoint based on whether it's own profile or other user
        const currentUser = JSON.parse(localStorage.getItem("user"));
        const isOwnProfile =
          currentUser &&
          (currentUser.id === userId || currentUser._id === userId);

        const endpoint = isOwnProfile
          ? `${API_BASE_URL}/research-rooms/my-rooms?${params}`
          : `${API_BASE_URL}/research-rooms/user/${userId}?${params}`;

        const response = await fetch(endpoint, { headers: getAuthHeaders() });
        const data = await handleResponse(response);

        if (!data.success) {
return [];
        }

        // Process the rooms data to add proper member counts and roles
        const rooms = data.data || [];
const processedRooms = rooms.map((room) => {
          // Calculate member count properly
          const memberCount =
            room.memberCount ||
            room.membersCount ||
            (room.members ? room.members.length : 0);

          // Determine if current user is the creator/leader
          const isCreator =
            room.leader &&
            (room.leader._id === currentUser?.id ||
              room.leader.id === currentUser?.id ||
              room.leader._id === currentUser?._id ||
              room.leader.id === currentUser?._id);

          // Find user's membership info
          let userMembership = null;
          if (room.members && Array.isArray(room.members)) {
            userMembership = room.members.find(
              (member) =>
                member.user?._id === currentUser?.id ||
                member.user?.id === currentUser?.id ||
                member.user?._id === currentUser?._id ||
                member.user?.id === currentUser?._id ||
                member._id === currentUser?.id ||
                member.id === currentUser?.id
            );
          }

          return {
            ...room,
            // Standardize field names
            membersCount: memberCount,
            memberCount: memberCount,
            isCreator: isCreator,
            isLeader: isCreator,
            // Add user's role and join date
            userRole: isCreator ? "creator" : userMembership?.role || "member",
            joinedAt: userMembership?.joinedAt || room.createdAt,
            // Add status flags
            isActive: room.status === "active",
            status: room.status || "active",
            // Add preview URL for navigation (use backend's or generate fallback)
            previewUrl: room.previewUrl || `/labs/${room._id || room.id}/preview`,
          };
        });
return processedRooms;
      } catch (error) {
return [];
      }
    },
  },

  // Communities
  communities: {
    // Get user's communities - UPDATED ENDPOINT
    getUserCommunities: async (userId, status = "all") => {
      try {
        const params = new URLSearchParams({
          status,
        });

        const response = await fetch(
          `${API_BASE_URL}/communities/user/${userId}?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        
        // Handle various response structures
        const communities = data?.data?.communities || data?.data || data?.communities || [];
        
        if (!Array.isArray(communities)) {
return [];
        }
        
        // Process communities to add previewUrl
        return communities.map(community => ({
          ...community,
          previewUrl: community.previewUrl || `/communities/${community._id || community.id}/preview`,
        }));
      } catch (error) {
return []; // Return empty array instead of throwing
      }
    },
  },

  // Workshops
  workshops: {
    // Get user's workshops - UPDATED ENDPOINT
    getUserWorkshops: async (userId, status = "all") => {
      try {
        const params = new URLSearchParams({
          status,
        });

        const response = await fetch(
          `${API_BASE_URL}/workshops/user/${userId}?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        
        // Handle various response structures
        const workshops = data?.data?.workshops || data?.data || data?.workshops || [];
        
        if (!Array.isArray(workshops)) {
return [];
        }
        
        // Process workshops to add previewUrl
        return workshops.map(workshop => ({
          ...workshop,
          previewUrl: workshop.previewUrl || `/workshops/${workshop._id || workshop.id}`,
        }));
      } catch (error) {
return []; // Return empty array instead of throwing
      }
    },
  },

  // Activity Feed
  activity: {
    // Get user's activity timeline
    getUserActivity: async (userId, page = 1, limit = 20) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/users/${userId}/activity?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Get recent activity summary
    getActivitySummary: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/users/${userId}/activity/summary`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : null;
      } catch (error) {
throw error;
      }
    },
  },

  // Profile Stats
  stats: {
    // Get comprehensive profile statistics - UPDATED TO USE PROFILE ENDPOINT
    getProfileStats: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/profile/${userId}`,
          {
            headers: getAuthHeaders(),
          }
        );
        const data = await handleResponse(response);
        // Extract profileStats from the profile response
        return data.success && data.data
          ? data.data.profile?.profileStats
          : null;
      } catch (error) {
throw error;
      }
    },

    // Get engagement statistics
    getEngagementStats: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/users/${userId}/engagement`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : null;
      } catch (error) {
throw error;
      }
    },
  },

  // Social Features
  social: {
    // Get followers with relationship context
    getFollowers: async (userId, page = 1, limit = 20) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/collaborators/followers/${userId}?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Get following with relationship context
    getFollowing: async (userId, page = 1, limit = 20) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/collaborators/following/${userId}?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Follow/Unfollow user
    followUser: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/follow/${userId}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
          }
        );
        const data = await handleResponse(response);
        return data.success;
      } catch (error) {
throw error;
      }
    },

    unfollowUser: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/follow/${userId}`,
          {
            method: "DELETE",
            headers: getAuthHeaders(),
          }
        );
        const data = await handleResponse(response);
        return data.success;
      } catch (error) {
throw error;
      }
    },

    // Remove follower (when you remove someone following YOU)
    removeFollower: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/followers/${userId}`,
          {
            method: "DELETE",
            headers: getAuthHeaders(),
          }
        );
        const data = await handleResponse(response);
        return data.success;
      } catch (error) {
throw error;
      }
    },
  },

  // Skills & Endorsements
  skills: {
    // Get user's skills with endorsements
    getUserSkills: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/skills/${userId}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Get endorsement statistics
    getEndorsementStats: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/endorsements/${userId}/stats`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : null;
      } catch (error) {
throw error;
      }
    },

    // Endorse a skill
    endorseSkill: async (userId, skillName, level = null) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/collaborators/endorse/${userId}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ skill: skillName, level }),
          }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : null;
      } catch (error) {
throw error;
      }
    },
  },

  // Research & Publications
  research: {
    // Get user's publications/papers
    getUserPublications: async (userId, page = 1, limit = 10) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/users/${userId}/publications?${params}`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },

    // Get research interests with context
    getResearchInterests: async (userId) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/users/${userId}/research-interests`,
          { headers: getAuthHeaders() }
        );
        const data = await handleResponse(response);
        return data.success ? data.data : [];
      } catch (error) {
throw error;
      }
    },
  },

  // Complete Profile Data Loading - OPTIMIZED
  loadCompleteProfile: async (userId) => {
    try {
      // Use the optimized parallel loading approach
      const [profile, posts, communities, workshops, rooms] = await Promise.all(
        [
          fetch(`${API_BASE_URL}/collaborators/profile/${userId}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/posts/user/${userId}?limit=5`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/communities/user/${userId}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/workshops/user/${userId}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/research-rooms/user/${userId}`, {
            headers: getAuthHeaders(),
          }),
        ]
      );

      return {
        profile: await handleResponse(profile),
        posts: await handleResponse(posts),
        communities: await handleResponse(communities),
        workshops: await handleResponse(workshops),
        rooms: await handleResponse(rooms),
      };
    } catch (error) {
throw error;
    }
  },
};

export default professionalProfileAPI;
